import { ORG_MFA_REDIRECT_REASON } from "@shared/constants";

/** Route where users enrol in / manage multi-factor authentication. */
export const MANAGE_MFA_PATH = "/account/manage-mfa";

/**
 * Routes that stay reachable while the organisation MFA guard is blocking
 * everything else. Without these the guard would trap the user in a redirect
 * loop (manage-mfa) or prevent them from signing out / switching organisation.
 */
export const MFA_GUARD_EXEMPT_PATHS: readonly string[] = [
  MANAGE_MFA_PATH,
  "/logout",
  "/onboarding",
  "/choose-organization",
  "/accept-invitation",
];

export function buildManageMfaRedirect(reason: string = ORG_MFA_REDIRECT_REASON): string {
  return `${MANAGE_MFA_PATH}?reason=${encodeURIComponent(reason)}`;
}

export function isMfaGuardExemptPath(pathname: string | undefined): boolean {
  if (!pathname) return false;
  return MFA_GUARD_EXEMPT_PATHS.some(
    (exempt) => pathname === exempt || pathname.startsWith(`${exempt}/`)
  );
}

export interface OrgMfaGateInput {
  /** False while Clerk is still resolving the user/organisation. */
  isLoaded: boolean;
  /** `publicMetadata.requireMfa` resolved for the active organisation. */
  orgRequiresMfa: boolean;
  /** Clerk `user.twoFactorEnabled`. */
  userHasMfa: boolean;
  /** Current location, used to keep the manage-MFA and sign-out routes reachable. */
  pathname?: string;
}

/**
 * True when the active organisation requires MFA and the signed-in user has not
 * enabled it, so org content must be blocked until they do.
 */
export function shouldBlockForOrgMfa({
  isLoaded,
  orgRequiresMfa,
  userHasMfa,
  pathname,
}: OrgMfaGateInput): boolean {
  if (!isLoaded) return false;
  if (!orgRequiresMfa) return false;
  if (userHasMfa) return false;
  return !isMfaGuardExemptPath(pathname);
}
